// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';

@Injectable()
export class LMApplicationMenuSearchService {
  static readonly NO_MENU_SEARCH_LIMIT = 5;

  public menuNameMatch = false;

  constructor(private lmUrlUtilService: LMUrlUtilService) {}

  public getMenuSearchUrl(menuSearchString: string): string {
    let menuSearchUrl = this.lmUrlUtilService.getBannerSpecUrl();

    const delimiter = (menuSearchUrl.indexOf('?') < 0) ? '?' : '&';
    menuSearchUrl += delimiter + 'requestType=searchMenu&menuSearch=' + menuSearchString;

    return menuSearchUrl;
  }

  public matchMultiMenus(menuSpecs: Array<LMWebAppMenuSpec>, searchString: string, applicationMenuSearchInfo?: LMApplicationMenuSearchInfo): {[ key: string ]: Array<LMApplicationMenuSearchResult>} {
    if (!applicationMenuSearchInfo) {
      applicationMenuSearchInfo = this.getApplicationMenuSearchInfo(searchString);
    }

    const menuResult: { [ key: string ]: Array<any> } = {};

    menuSpecs.forEach(menuSpec => {
      let searchResult: Array<LMApplicationMenuSearchResult> = [];

      if (menuSpec.webAppName !== 'web') {
        if (!applicationMenuSearchInfo.limitSearch  || searchResult.length < LMApplicationMenuSearchService.NO_MENU_SEARCH_LIMIT) {
          searchResult = this.matchMenus(menuSpec.menuSpecs, applicationMenuSearchInfo.searchString, applicationMenuSearchInfo);
          if (searchResult.length > 0) {
            menuResult [ menuSpec.webAppName ] = searchResult;
          }
        }
      }
    });

    return menuResult;
  }

  private matchMenus(menuSpecs: Array<LMMenuItemSpec>, searchString: string, applicationMenuSearchInfo: LMApplicationMenuSearchInfo): Array<LMApplicationMenuSearchResult> {
    const searchResult: Array<LMApplicationMenuSearchResult> = [];

    if (!menuSpecs) {
      return searchResult;
    }

    for (let i = 0; i < menuSpecs.length; i++) {
      if (applicationMenuSearchInfo.limitSearch && searchResult.length >= LMApplicationMenuSearchService.NO_MENU_SEARCH_LIMIT) {
        break;
      }

      this.findMenuMatch(searchResult, menuSpecs[ i ], searchString, applicationMenuSearchInfo);

      this.menuNameMatch = false;
    }
    searchResult.sort((menu1: LMApplicationMenuSearchResult, menu2: LMApplicationMenuSearchResult) =>
      menu1.child.localeCompare(menu2.child));

    return searchResult;
  }

  public matchData(dataMenus: Array<LMApplicationMenuDataMenu>): Array<LMApplicationMenuSearchResult> {
    const searchResult: Array<LMApplicationMenuSearchResult> = [];

    if (dataMenus) {
      for (let i = 0; i < dataMenus.length; i++) {
        this.findDataMatch(searchResult, dataMenus[ i ]);
      }
    }
    return searchResult;
  }

  public matchReports(reports: Array<LMApplicationMenuReportMenu>): Array<LMApplicationMenuSearchResult> {
    const searchResult: Array<LMApplicationMenuSearchResult> = [];

    if (reports) {
      reports.forEach(report => this.findReportMatch(searchResult, report));
    }

    return searchResult;
  }

  public matchMenuOptions(menuOptions, searchString: string): Array<LMApplicationMenuSearchResult> {
    const searchResult: Array<LMApplicationMenuSearchResult> = [];
    const applicationMenuSearchInfo = this.getApplicationMenuSearchInfo(searchString);

    for (let i = 0; i < menuOptions.length; i++) {
      const menuSpecLabel: string = menuOptions[i].label as string;

      if (applicationMenuSearchInfo.searchType === 'STARTSWITH') {
        searchString = '^' + searchString;
      } else if (applicationMenuSearchInfo.searchType === 'ENDSWITH') {
        searchString = searchString + '$';
      }

      const pattern = this.generatePattern(searchString);

      if (pattern.test(menuSpecLabel)) {
        const result: LMApplicationMenuSearchResult = {
          id: menuOptions[i].id,
          automationId: menuOptions[i].automationId,
          type: 'Applications',
          parent: '',
          child: menuOptions[i].label,
          url: menuOptions[i].url
        };
        searchResult.push(result);
      }
    }

    return searchResult;
  }

  // todo vonnyw - element id project - how to type menuSpec? why is it not just LMMenuItemSpec?
  private findMenuMatch(searchResult: Array<LMApplicationMenuSearchResult>, menuSpec: any, searchString: string, applicationMenuSearchInfo: LMApplicationMenuSearchInfo): void {

    if (menuSpec.menu) {
      for (let i = 0; i < menuSpec.menu.length; i++) {
        if (applicationMenuSearchInfo.limitSearch && searchResult.length >= LMApplicationMenuSearchService.NO_MENU_SEARCH_LIMIT) {
          break;
        }

        if (!this.menuNameMatch) {
          this.matchMenuName(searchResult, menuSpec, searchString, applicationMenuSearchInfo);
        }
        this.findMenuMatch(searchResult, menuSpec.menu[ i ], searchString, applicationMenuSearchInfo);
      }

    } else {
      const menuSpecLabel: string = menuSpec.label as string;

      if (applicationMenuSearchInfo.searchType === 'STARTSWITH') {
        searchString = '^' + searchString;
      } else if (applicationMenuSearchInfo.searchType === 'ENDSWITH') {
        searchString = searchString + '$';
      }

      const pattern = this.generatePattern(searchString);

      if (pattern.test(menuSpecLabel)) {
        const result: LMApplicationMenuSearchResult = {
          id: menuSpec.id,
          automationId: menuSpec.automationId,
          type: 'Applications',
          parent: menuSpec.parentLabel,
          child: menuSpec.label,
          url: menuSpec.link
        };
        searchResult.push(result);
      }

    }
    this.matchSearchTerm(searchResult, menuSpec, searchString, applicationMenuSearchInfo);
  }

  private generatePattern(searchString: string): RegExp {

    const pattern = new RegExp(searchString.replace(/\*/g, '.*')
        .replace('\\', '\\\\').replace(/\%/g, '\\u0025')
        .replace(/\(/g, '\\u0028').replace(/\)/g, '\\u0029')
        .replace(/\[/g, '\\u005B'), 'i');

    return pattern;
  }

  private findReportMatch(searchResult: Array<LMApplicationMenuSearchResult>, report: LMApplicationMenuReportMenu): void {
    searchResult.push({
      id: report.id,
      automationId: report.automationId,
      type: report.type,
      child: report.name,
      url: report.url
    });
  }

  private findDataMatch(searchResult: Array<LMApplicationMenuSearchResult>, dataMenu: LMApplicationMenuDataMenu): void {
    if (dataMenu.type === 'CLASS') {
      searchResult.push({
        automationId: dataMenu.automationId,
        id: LMApplicationMenuSearchService.toHtmlElementId(dataMenu.id),
        type: 'Data',
        parent: dataMenu.moduleName + ' / ' + 'Business Classes',
        child: dataMenu.name,
        url: dataMenu.url
      });
    } else if (dataMenu.type === 'TASK') {
      searchResult.push({
        automationId: dataMenu.automationId,
        id: LMApplicationMenuSearchService.toHtmlElementId(dataMenu.id),
        type: 'Data',
        parent: dataMenu.moduleName + ' / ' + 'Business Tasks' + ' / ' + dataMenu.name,
        child: dataMenu.name + '.' + dataMenu.command,
        url: dataMenu.url,
        resourceId: dataMenu.resourceId
      });
    }
  }

  public getApplicationMenuSearchInfo(menuSearchValue: string): LMApplicationMenuSearchInfo {
    let menuSearchType: LMApplicationMenuSearchType = null;
    let menuSearchString: string = null;

    if (menuSearchValue.startsWith('*')) {
      menuSearchType = 'STARTSWITH';
      menuSearchString = menuSearchValue.substring(1, menuSearchValue.length);
    } else if (menuSearchValue.endsWith('*')) {
      menuSearchType = 'ENDSWITH';
      menuSearchString = menuSearchValue.substring(0, menuSearchValue.length - 1);
    } else {
      menuSearchType = 'CONTAINS';
      menuSearchString = menuSearchValue;
    }

    return {
      searchString: menuSearchString,
      searchType: menuSearchType,
      limitSearch: menuSearchValue.length <= 2 ? true : false
    };
  }

  /**
   * @param accessibleWebApps
   * @param searchResultsByWebApp { [webAppName]: LMBannerSearchResults[] }
   * @param currentWebApp
   */
  public buildWebAppSpecsFromSearchResults(
    accessibleWebApps: LMWebAppSpec[],
    searchResultsByWebApp: { [ key: string ]: LMBannerSearchResult[] },
    currentWebApp: string
  ): LMWebAppSpec[] {
    const webAppsWithSearchResults: LMWebAppSpec[] = [];

    Object.keys(searchResultsByWebApp).forEach(webAppName => accessibleWebApps.find(webAppSpec => {
      if (webAppSpec.webAppName === webAppName) {
        if (webAppName === currentWebApp) {
          webAppsWithSearchResults.splice(0, 0, webAppSpec);
        } else {
          webAppsWithSearchResults.push(webAppSpec);
        }
      }
    }));

    return webAppsWithSearchResults;
  }

  private matchMenuName(searchResult: Array<LMApplicationMenuSearchResult>, menuSpec: any, searchString: string, applicationMenuSearchInfo: LMApplicationMenuSearchInfo): void {

    const menuSpecLabel: string = menuSpec.menu[0].parentLabel as string;
    if (applicationMenuSearchInfo.searchType === 'STARTSWITH') {
      searchString = '^' + searchString;
    } else if (applicationMenuSearchInfo.searchType === 'ENDSWITH') {
      searchString = searchString + '$';
    }

    const pattern = this.generatePattern(searchString);

    if (pattern.test(menuSpecLabel) && (menuSpec.menu && menuSpec.menu.length > 0)) {
      const result: LMApplicationMenuSearchResult = {
        id:           menuSpec.id,
        automationId: menuSpec.automationId,
        type:         'Applications',
        parent:       menuSpec.parentLabel,
        child:        menuSpec.label,
        url:          menuSpec.link,
        menu:         menuSpec.menu
      };
      searchResult.push(result);
      this.menuNameMatch = true;
    }
  }

  private matchSearchTerm(searchResult: Array<LMApplicationMenuSearchResult>, menuSpec: any, searchString: string, applicationMenuSearchInfo: LMApplicationMenuSearchInfo): void {

    // looks for search terms defined in .menu at each menu item defined.
    // TODO look at combining .menuItemTerms and .menuTerms in MenuViewBuilder
    if (menuSpec.menuItemTerms) {
      for (let n = 0; n < menuSpec.menuItemTerms.length; n++) {
        const searchTerm: string = menuSpec.menuItemTerms[n] as string;
        const match = this.matchTerm(searchResult, menuSpec, searchString, applicationMenuSearchInfo, searchTerm);
        if (match) {break;}
      }
    }

    // looks for search terms defined in .menu at the menu level.
    if (menuSpec.menuTerms) {
      for (let n = 0; n < menuSpec.menuTerms.length; n++) {
        const searchTerm: string = menuSpec.menuTerms[n] as string;
        const match = this.matchTerm(searchResult, menuSpec, searchString, applicationMenuSearchInfo, searchTerm);
        if (match) {break;}
      }
    }

    // looks for search terms defined in page and form panels.
    if (menuSpec?.panelSearchTerms) {
      for (let n = 0; n < menuSpec.panelSearchTerms.length; n++) {
        const panel = menuSpec.panelSearchTerms[n];
        if (panel?.searchTerms) {
          const terms = panel?.searchTerms;

          for (let x = 0; x < terms.length; x++) {
            const searchTerm: string = terms[x] as string;
            const match = this.matchTerm(searchResult, panel, searchString, applicationMenuSearchInfo, searchTerm);
            if (match) {break;}
          }
        }
      }
    }
  }

  private matchTerm(searchResult: Array<LMApplicationMenuSearchResult>, menuSpec: any, searchString: string, applicationMenuSearchInfo: LMApplicationMenuSearchInfo, searchTerm: string): Boolean {

    let matchFound = false;

    if (applicationMenuSearchInfo.searchType === 'STARTSWITH') {
      searchString = '^' + searchString;
    } else if (applicationMenuSearchInfo.searchType === 'ENDSWITH') {
      searchString = searchString + '$';
    }

    const pattern = this.generatePattern(searchString);

    if (pattern.test(searchTerm)) {
      matchFound = true;
      const result: LMApplicationMenuSearchResult = {
        id:           menuSpec.id,
        automationId: menuSpec.automationId,
        type:         'Applications',
        parent:       menuSpec.parentLabel,
        child:        menuSpec.label,
        url:          menuSpec.link,
        menu:         (menuSpec.menu && menuSpec.menu.length > 0) ? menuSpec.menu : null,
      };
      searchResult.push(result);
    }

    return matchFound;
  };

  private static toHtmlElementId(id: string) {
    return id.replace('.', '_');
  }
}
